import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    SimpleGrid,
    List,
    ListItem,
    Stack,
    Spinner,
    Table,
    Th,
    Td,
    Tr,
    Thead,
    Tbody
} from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { onSettingsChange } from '../redux/Features/Settings';
import { useEffect, useState } from 'react';
import { getActiveHouseModelsList, getManagerThumbs, getSpecificManagerData } from '../utils/webiny';

// maps what your UI shows → what Excel uses
export const MODEL_ABBR_CANONICAL = {
    // Conservative – Capital Preservation
    USBFM: 'Conservative Models for Capital Preservation',
    CPBFM: 'Conservative Models for Capital Preservation',
    SBDFM: 'Conservative Models for Capital Preservation',
    REIFM: 'Conservative Models for Capital Preservation',

    // Blend Models
    IDVFM: 'Blend Models',
    HRDFM: 'Blend Models',
    COMFM: 'Blend Models',
    ESGFM: 'Blend Models',

    // Aggressive – Capital Growth
    INGFM: 'Aggressive Models for Capital Growth',
    TMGFM: 'Aggressive Models for Capital Growth',
    GLVFM: 'Aggressive Models for Capital Growth',
    JATFM: 'Aggressive Models for Capital Growth',
    EGIFM: 'Aggressive Models for Capital Growth',
    DWVFM: 'Aggressive Models for Capital Growth',
    KHFFM: 'Aggressive Models for Capital Growth',
    GBFFM: 'Aggressive Models for Capital Growth',
};

export default function Investments({ onModalOpen }) {
    let navigate = useNavigate();
    const dispatch = useDispatch();

    const [Managers, setManagers] = useState();
    const [HouseModels, setHouseModels] = useState();
    console.log(Managers);
    const stripAbbrevFromFund = (fund = '', abbrev = '') => {
        if (!abbrev) return fund;

        return fund
            .replace(abbrev, '')   // remove "(HFF)"
            .trim();               // clean extra spaces
    }
    const parseReturns = (returnsString = '') => {
        const map = {
            mtd: /MTD:\s*(-?\d+(\.\d+)?)/,
            ytd: /YTD:\s*(-?\d+(\.\d+)?)/,
            '1y': /1Y:\s*(-?\d+(\.\d+)?)/,
            '3y': /3Y(R)?:\s*(-?\d+(\.\d+)?)/,   // 👈 FIX
            '5y': /5Y:\s*(-?\d+(\.\d+)?)/,
        };

        const result = {};

        Object.keys(map).forEach((key) => {
            const match = returnsString.match(map[key]);
            result[key] = match ? parseFloat(match[1]) : null;
        });

        return result;
    }

    const getCategoryFromName = (name) => {
        const n = name.toLowerCase();

        // Conservative – Capital Preservation
        if (
            n.includes('bond') ||
            n.includes('reit') ||
            n.includes('stable')
        ) {
            return 'Conservative Models for Capital Preservation';
        }

        // Blend Models
        if (
            n.includes('dividend') ||
            n.includes('commodities') ||
            n.includes('esg')
        ) {
            return 'Blend Models';
        }

        // Aggressive – Capital Growth
        if (
            n.includes('growth') ||
            n.includes('value') ||
            n.includes('focus') ||
            n.includes('future') ||
            n.includes('tartan') ||
            n.includes('investment') ||
            n.includes('capital') ||
            n.includes('fund')
        ) {
            return 'Aggressive Models for Capital Growth';
        }

        // Fallback
        return 'Other Models';
    };

    const parseField = (string = "", key = "") => {
        const regex = new RegExp(`${key}(?:R)?:\\s*([\\d.-]+|na)%?`, "i");
        const match = string.match(regex);

        if (!match) return "-";

        // handle "na"
        if (match[1].toLowerCase() === "na") return "-";

        return match[1] + "%";
    };

    const normalizeFundModel = ({
        name,
        abbr,
        returns,
        returnsThru,
        oneYear = 0,
        threeYear = 0,
        fiveYear = 0,
        label = "",
        type = ""
    }) => {


        return {
            name,
            abbr,
            category: getCategoryFromName(name),
            returnsThru,
            returns: {
                ...returns,
                oneYear,
                threeYear,
                fiveYear
            },
            label,
            type
        };
    };

    const normalizeManager = (manager) => {
        const cleanFundName = manager.fund
            .replace(manager.abbrev, '')
            .trim();

        const abbr = manager.abbrev.replace(/[()]/g, '');

        return normalizeFundModel({
            name: `${manager.name}'s ${cleanFundName}`,
            abbr,
            returns: parseReturns(manager.returnsNumber || ''),
            returnsThru: manager.returnsThru || '',
            oneYear: parseField(manager.returnsNumber, "1Y"),
            threeYear: parseField(manager.returnsNumber, "3y"),
            fiveYear: parseField(manager.returnsNumber, "5y"),
            label: manager.name,
            type: "manager"
        });
    }

    const normalizeHouseModel = (model) => {
        return normalizeFundModel({
            label: model.allocationMetaData.label,
            name: model.allocationMetaData.label + ' Fund Model',
            abbr: model.allocationMetaData.value,
            returns: parseReturns(model.returnsNumber || ''),
            returnsThru: model.returnsThru || '',
            oneYear: parseField(model.returnsNumber, "1Y"),
            threeYear: parseField(model.returnsNumber, "3y"),
            fiveYear: parseField(model.returnsNumber, "5y"),
            type: "house"
        });
    }

    const ReturnCell = ({ value }) => (
        <Text color={value > 0 ? 'green.500' : value < 0 ? 'red.500' : 'gray.400'}>
            {value !== null ? `${value}%` : '-'}
        </Text>
    );

    const setManagersAndModels = async () => {

        // set up form with active managers and models from webiny
        await getManagerThumbs()
            .then(async (res) => {
                //console.log(res);
                const thumbs = await getManagerThumbs();

                let managers = thumbs.data.data.listManagers.data;

                managers = await Promise.all(
                    managers.map(async (m) => {
                        const res2 = await getSpecificManagerData(m.name);
                        console.log("res2: ", res2.data.data.getManagers.data.returnsThru);

                        if (res2.data && res2.data.data) {
                            const returnsNumber = res2.data.data.getManagers.data.returnsNumber[0];
                            const returnsThru = res2.data.data.getManagers.data.returnsThru;
                            return {
                                ...m,
                                returnsNumber: returnsNumber,
                                returnsThru: returnsThru
                            };
                        }

                        return m;
                    })
                );

                managers.push(' ');
                setManagers(managers);
            })
            .catch((err) =>
                console.log(
                    `setManagersAndModels failed on Allocations Page, ${err}`
                )
            );
        await getActiveHouseModelsList()
            .then((res) => {
                let hms = res.data.data.listHouseModels.data;
                console.log(hms);
                setHouseModels(hms);
            })
            .catch((err) =>
                console.log(
                    `setManagersAndModels failed on Allocations Page, ${err}`
                )
            );
    };

    useEffect(() => {
        setManagersAndModels();
    }, []);

    if (!HouseModels || !Managers) {
        return <Box marginTop={"20px"}><Text fontSize={"28pxnpm "}>Loading...</Text></Box>
    }

    const tdstyle = {
        height: "25px",
        lineHeight: "25px",
        padding: "0 8px",
        whiteSpace: "nowrap",
    };

    const getCategoryStyle = (name = "", groupedRows, index) => {
        const isLast = index === groupedRows[name].length - 1;
        if (!isLast) return {
            height: "1px",
        }
        switch (name) {
            case "Conservative Models for Capital Preservation":
            case "Blend Models":
            case "Aggressive Models for Capital Growth":
                return {
                    borderBottom: "3px solid #fff",
                    height: "1px",
                }
            default:
                return {
                    height: "1px",
                }
        }
    }

    const rows = [
        ...HouseModels.map(normalizeHouseModel),
        ...Managers.filter(m => m !== ' ').map(normalizeManager),
    ];
    console.log(rows);

    const groupedRows = rows.reduce((acc, row) => {
        acc[row.category] = acc[row.category] || [];
        acc[row.category].push(row);
        return acc;
    }, {});

    const formatPercent = (val) => {
        if (val === null || val === undefined || val === '-') return '-';

        // already formatted
        if (typeof val === 'string' && val.includes('%')) return val;

        // numeric → add %
        if (!isNaN(val)) return `${val}%`;

        return val;
    };

    return (
        <>
            <Box position={'relative'} w="83%" borderBottomRadius={'xl'}>
                <Container
                    maxW={'8xl'}
                    zIndex={10}
                    position={'relative'}
                    borderRadius={'xl'}
                >
                    <Stack
                        flex={1}
                        color={'mkyfive.900'}
                        justify={{ lg: 'center' }}
                        py={{ base: 4, md: 5, xl: 5 }}
                    >
                        <Box mb={{ base: 8, md: 8 }}>
                            <Heading
                                color={'mkytwo.500'}
                                mb={2}
                                fontSize={{ base: '3xl', md: '3xl' }}
                                textAlign={'left'}
                            >
                                Investing with Us
                            </Heading>
                            <Container
                                maxW={'8xl'}
                                zIndex={10}
                                position={'relative'}
                                bg={'mkytwo.500'}
                                borderRadius={'xl'}
                                boxShadow="dark-lg"
                            >
                                <Text
                                    fontSize={'lg'}
                                    color={'mkyfive.50'}
                                    textAlign={'left'}
                                    pt={3}
                                    px={'.75em'}
                                >
                                    Investing is different for everyone
                                    depending on multiple factors such as your
                                    career, income, age, risk tolerance and
                                    more. If you would answer some questions to
                                    help us understand your individual
                                    situation, we will provide you with our
                                    recommendation of how you should invest your
                                    money across our managers and models.
                                </Text>
                                <Button
                                    rounded={'full'}
                                    fontWeight={'normal'}
                                    bg="mkyfive.50"
                                    _hover={{
                                        bg: 'mkytwo.500',
                                        color: 'mkyfive.50',
                                        border: '2px',
                                        borderColor: 'mkyfive.50',
                                        boxShadow: 'dark-lg',
                                    }}
                                    px={6}
                                    my={5}
                                    onClick={() => {
                                        dispatch(
                                            onSettingsChange({
                                                name: 'ModalType',
                                                value: 'Questions',
                                            })
                                        );
                                        onModalOpen();
                                    }}
                                >
                                    Yes, show me please!
                                </Button>
                                <Text
                                    fontSize={'lg'}
                                    color={'mkyfive.50'}
                                    textAlign={'left'}
                                    px={'.75em'}
                                >
                                    You can learn more about all our investment
                                    models by clicking on them below. Our
                                    portfolio managers each manage their own
                                    unique model fund and our Marketocracy
                                    Masters (MM) models are managed by teams of
                                    our managers that comprise our investment
                                    committee.
                                </Text>
                                <Button
                                    rounded={'full'}
                                    fontWeight={'normal'}
                                    bg="mkyfive.50"
                                    _hover={{
                                        bg: 'mkytwo.500',
                                        color: 'mkyfive.50',
                                        border: '2px',
                                        borderColor: 'mkyfive.50',
                                        boxShadow: 'dark-lg',
                                    }}
                                    px={6}
                                    my={5}
                                    onClick={() =>
                                        navigate('/allocations', {
                                            state: { from: 'Skip Form' },
                                        })
                                    }
                                >
                                    Open Account
                                </Button>
                            </Container>
                        </Box>
                    </Stack>
                </Container>
            </Box>
            <Container w="83%" maxW={'8xl'} zIndex={10} position={'relative'}>
                <Heading
                    color={'mkytwo.500'}
                    mb={5}
                    fontSize={{ base: '3xl', md: '3xl' }}
                    textAlign={'left'}
                >
                    Investment Choices
                </Heading>
                {/* {HouseModels && Managers ? (
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
                        <List
                            border="4px"
                            borderRadius={'xl'}
                            borderColor={'mkyone.700'}
                            boxShadow="dark-lg"
                        >
                            <ListItem>
                                <Text
                                    fontSize={'lg'}
                                    w={'100%'}
                                    py={2}
                                    bg={'mkyone.700'}
                                    color={'mkyfive.50'}
                                >
                                    House Models
                                </Text>
                            </ListItem>
                            {HouseModels.map((model, i) => (
                                <ListItem key={i}>
                                    <Button
                                        w={'100%'}
                                        _hover={{
                                            bg: 'mkyfive.50',
                                            color: 'mkytwo.900',
                                            boxShadow: 'dark-lg',
                                        }}
                                        borderRadius={0}
                                        bg="mkytwo.500"
                                        color="mkyfive.50"
                                        onClick={() =>
                                            navigate(
                                                `/house/${model.allocationMetaData.label}`
                                            )
                                        }
                                    >
                                        {model.allocationMetaData.label}
                                    </Button>
                                </ListItem>
                            ))}
                        </List>
                        <List
                            border="4px"
                            borderRadius={'xl'}
                            borderColor={'mkyone.700'}
                            boxShadow="dark-lg"
                        >
                            <Text
                                fontSize={'lg'}
                                w={'100%'}
                                py={2}
                                bg={'mkyone.700'}
                                color={'mkyfive.50'}
                            >
                                Portfolio Managers
                            </Text>
                            {Managers.map((manager, i) =>
                                manager !== ' ' ? (
                                    <ListItem key={i}>
                                        <Button
                                            w={'100%'}
                                            borderRadius={0}
                                            bg="mkytwo.500"
                                            color="mkyfive.50"
                                            _hover={{
                                                bg: 'mkyfive.50',
                                                color: 'mkytwo.900',
                                                boxShadow: 'dark-lg',
                                            }}
                                            onClick={() =>
                                                navigate(
                                                    `/manager/${manager.name}`
                                                )
                                            }
                                        >
                                            {manager.name}
                                        </Button>
                                    </ListItem>
                                ) : (
                                    <ListItem key={i}>
                                        <Button
                                            w={'100%'}
                                            borderRadius={0}
                                            bg="mkytwo.500"
                                            color="mkyfive.50"
                                            _hover={{
                                                bg: 'mkytwo.500',
                                                color: 'mkyfive.50',
                                            }}
                                        ></Button>
                                    </ListItem>
                                )
                            )}
                        </List>
                    </SimpleGrid>
                ) : (
                    <Spinner size={'xl'} />
                )} */}
                <Box
                    border="4px"
                    borderColor="mkyone.700"
                    borderRadius="xl"
                    boxShadow="dark-lg"
                    overflowX="auto"
                >
                    <Table variant="simple" size="sm" >
                        <Thead bg="mkyone.700">
                            <Tr>
                                <Th></Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>Investment Options</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>Model Abbr.</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>QTD</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>YTD</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>1 Year</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>3 Years</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>Since Inception</Th>
                                <Th color="mkyfive.50" fontSize={"10px"}>Net Return</Th>

                            </Tr>
                        </Thead>

                        <Tbody>
                            {Object.entries(groupedRows).map(([category, items]) =>
                                items.map((row, index) => (
                                    <Tr
                                        _hover={{
                                            backgroundColor: "#c1c1c1ff",
                                            cursor: "pointer"
                                        }}
                                        onClick={() =>
                                            navigate(
                                                `/${row.type}/${row.label}`
                                            )
                                        }
                                        style={getCategoryStyle(category, groupedRows, index)} key={`${category}-${index}`}
                                    >
                                        {/* SIDE LEGEND CELL */}
                                        {index === 0 && (
                                            <Td
                                                rowSpan={items.length}
                                                bg="mkyone.700"
                                                color="mkyfive.50"
                                                fontWeight="bold"
                                                textAlign="center"
                                                verticalAlign="middle"
                                                px={2}
                                            >
                                                <Box
                                                    sx={{
                                                        writingMode: "vertical-rl",
                                                        transform: "rotate(180deg)",
                                                        textAlign: "center",
                                                        whiteSpace: "normal",   // allow wrapping
                                                        lineHeight: "1.1",
                                                        fontSize: "12px",
                                                        height: `${items.length * 25}px`,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center"
                                                    }}
                                                >
                                                    {category}
                                                </Box>
                                            </Td>
                                        )}

                                        {/* DATA CELLS */}
                                        <Td><div style={{ ...tdstyle }} >{row.name}</div></Td>
                                        <Td><div style={tdstyle} >{row.abbr}</div></Td>
                                        <Td><div style={tdstyle}>{formatPercent(row.returns.mtd)}</div></Td>
                                        <Td><div style={tdstyle}>{formatPercent(row.returns.ytd)}</div></Td>
                                        <Td><div style={tdstyle}>{formatPercent(row.returns.oneYear)}</div></Td>
                                        <Td><div style={tdstyle}>{formatPercent(row.returns.threeYear)}</div></Td>
                                        <Td><div style={tdstyle}>{formatPercent(row.returns.fiveYear)}</div></Td>
                                    </Tr>
                                ))
                            )}

                            {/* Benchmark */}
                            <Tr bg="yellow.100">
                                <Td
                                    bg="mkyone.700"
                                    color="mkyfive.50"
                                    fontWeight="bold"
                                    textAlign="center"
                                >
                                    Benchmark
                                </Td>
                                <Td>S&P 500 Index</Td>
                                <Td>SPX</Td>
                                <Td>-</Td>
                                <Td>-</Td>
                                <Td>-</Td>
                                <Td>-</Td>
                                <Td>-</Td>
                                <Td>-</Td>
                            </Tr>
                        </Tbody>
                    </Table>
                    <Box
                        bg="mkyone.700"
                        padding={5}
                    >
                        <Text
                            color="mkyfive.50"
                            fontSize={"16px"}
                            fontStyle={"italic"}
                        >
                            All Performance Numbers are net of fees and as of:		9/30/2025
                        </Text>
                    </Box>
                </Box>

            </Container>
        </>
    );
}
